import { css, styled } from "@/root/stitches.config";
import { router } from "@inertiajs/react";
import { useWindowSize } from "@uidotdev/usehooks";
import { confirmAlert } from "react-confirm-alert";

import { Button } from "@/components/button";
import { Text } from "@/components/text";
import { isOrderLocked } from "@/utils/lockedOrders";

const Wrapper = styled("div", {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: "min-content",
});

const RemoveButton = styled("button", {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 1.5rem",
    backgroundColor: "transparent",
    border: "none",
    outline: "none",
    fontFamily: "$main",
    letterSpacing: 2,
    cursor: "pointer",
    color: "$tertiary",
    "&:hover": {
        color: "$secondary",
    },
    "@desktop": {
        height: "$button-desktop-height",
        fontSize: "$normal-desktop",
    },
    "@laptop": {
        height: "$button-laptop-height",
        fontSize: "$normal-laptop",
    },
    "@tablet": {
        height: "$button-tablet-height",
        fontSize: "$normal-tablet",
    },
    "@mobile": {
        height: "$button-mobile-height",
        fontSize: "$normal-mobile",
    },
});

function ConfirmRemoveElement({ removeItemUrl, onClose }) {
    function handleRemoveItem() {
        onClose();
        router.delete(removeItemUrl);
    }

    return (
        <div
            className={css({
                position: "absolute",
                top: "50%",
                left: "50%",
                display: "flex",
                flexDirection: "column",
                gap: "2rem 1.5rem",
                transform: "translate(-50%, -50%)",
                width: "fit-content",
                height: "fit-content",
                padding: "1.5rem",
                backgroundColor: "$white",
                borderRadius: "0.5rem",
                zIndex: 99,
            }).toString()}
        >
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    textAlign: "center",
                }}
            >
                <Text css={{ color: "$dark", fontSize: "2rem", overflow: "hidden" }}>
                    Remove it?
                </Text>
                <Text css={{ color: "$dark", fontSize: "1.25rem", overflow: "hidden" }}>
                    Are you sure you want to remove this item?
                </Text>
            </div>
            <div
                className={css({
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    height: "fit-content",
                    "@mobile": {
                        flexDirection: "column",
                        gap: "0.5rem",
                    },
                }).toString()}
            >
                <Button onClick={onClose} fullWidth>
                    No, Keep It!
                </Button>
                <Button color="light" onClick={handleRemoveItem} fullWidth>
                    Yes, Delete It!
                </Button>
            </div>
        </div>
    );
}

export default function TicketCard({ data }) {
    const { width } = useWindowSize();

    function handleRemoveOrder() {
        return confirmAlert({
            customUI: ({ onClose }) => (
                <ConfirmRemoveElement
                    removeItemUrl={data.remove_url}
                    onClose={onClose}
                />
            ),
            closeOnClickOutside: true,
            closeOnEscape: true,
            overlayClassName: css({
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "$dark",
                backdropFilter: "blur(6.6px)",
                zIndex: 98,
            }).toString(),
        });
    }

    const isLocked = isOrderLocked(data.order?.reference);

    return (
        <Wrapper>
            {width < 768 && !isLocked && (
                <RemoveButton onClick={handleRemoveOrder}>X</RemoveButton>
            )}
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    flex: 1,
                    alignItems: "flex-start",
                    textAlign: "left"
                }}
            >
                <Text css={{ color: "$dark", overflow: "hidden" }}>Ticket {data.activity.name}</Text>
                <Text
                    css={{
                        color: "$dark",
                        fontSize: "1rem",
                        overflow: "hidden",
                        "@mobile": {
                            fontSize: "0.7rem"
                        }
                    }}
                >
                    Activity - {data.activity.dateStr}
                </Text>
            </div>
            <div
                className={css({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: "2rem",
                    "@mobile": { gap: "0rem" },
                }).toString()}
            >
                {width > 768 && (
                    <Text
                        css={{
                            color: "$dark",
                            fontSize: "1.5rem",
                            "@mobile": { fontSize: "1.25rem" },
                        }}
                    >
                        Rp {data.price.toLocaleString("id-ID")}
                    </Text>
                )}
                {!data.uuid && (
                    <Text
                        css={{
                            color: "$dark",
                            fontSize: "1.5rem",
                            textAlign: "right",
                            overflow: "hidden",
                            "@mobile": { fontSize: "0.7rem", wordBreak: "" },
                        }}
                    >
                        Pending
                    </Text>
                )}
                {width > 768 && !data.registration_id && !isLocked && (
                    <RemoveButton onClick={handleRemoveOrder}>
                        Hapus
                    </RemoveButton>
                )}
            </div>
        </Wrapper>
    );
}
